import { ForceInfo } from "@/types/processes/ForceInfo";
import {defineStore, storeToRefs} from "pinia";
import {ref, computed, watch, reactive} from "vue";
import {env} from "@fd/Environment.ts";
import {AgendaData} from "@t/agenda/Agenda.ts";
import {ApiErrorResponse} from "@t/util/ApiError.ts";
import {debounce} from "@/utils/Debounce.ts";

export const FI_BULK_LIMIT:number = 10;

function getLastForceInfoPosition() {
	const pos = localStorage.getItem("lastForceInfoPosition");
	if (pos != null) return parseInt(pos);
	return 0;
}

function saveLastForceInfoPosition(lastForceInfoPosition: number) {
	localStorage.setItem("lastForceInfoPosition", lastForceInfoPosition.toString());
}

/** Agenda data plus blocking users */
interface AgendaWithBlockers<T> extends AgendaData<T> {
	/** List of IDs of other users blocking this record */
	blockers: number[],
	/** True if current user blocks this record */
	userBlocks: boolean
}

export const useForceInfoStore = defineStore('forceInfo', () => {

	/** Resposible users of calendar */
	const responsibles = reactive<number[]>([]);
	const srcType = ref<number>(-1);			// must be -1 to detect initial change to 0 (watcher)
	const srcId = ref<number|string>(0);

	const forceInfoPosition = ref<number>(getLastForceInfoPosition());
	const forceInfoPage = ref<number>(5);
	const forceInfoTotal = ref<number>(0);

	const forceInfos = ref<ForceInfo[]>([]);
	const blockers = ref<number[]>([]);
	const userBlocks = ref<boolean>(false);


	function setForceInfoContext(type:number, id:number|string, respIDs:number[] = []):void {
		srcType.value = type;
		srcId.value = id;
		responsibles.length = 0;
		responsibles.push(...respIDs);
	}


	/**
	 * You can optionally limit results (filter) to certain responsibles or row objects
	 */
	function setResponsibles(ids: number[]):void {
		console.log("setResponsibles called");
		responsibles.length = 0;
		responsibles.push(...ids);
	}

	/** Return number of delegation feedbacks in forceinfos */
	const feedbackForceInSize = computed<number>(()=> {
		let i = 0;
		for (let fi of forceInfos.value) {
			if (fi.type === "fulfill-feedback") i++;
		}
		return i;
	});


	function setForceInfoItems(page: AgendaWithBlockers<ForceInfo>) {
		forceInfos.value.length = 0;
		for (let item of page.items) {
			forceInfos.value.push(item);
		}
		forceInfoTotal.value = page.total;
		forceInfoPage.value = page.size;
		forceInfoPosition.value = page.position;

		blockers.value.length = 0;
		blockers.value.push(...page.blockers);

		userBlocks.value = page.userBlocks;
	}

	const respList = computed<string>(() =>{
		if (responsibles.length > 0) return responsibles.join(',');
		return env.user.id.toString();
	});

	function addListEndpointParams(endpoint:string):string {
		endpoint = env.net.urlParam(endpoint, "responsibles", respList.value);
		endpoint = env.net.urlParam(endpoint, "position", forceInfoPosition.value);
		endpoint = env.net.urlParam(endpoint, "size", forceInfoPage.value);
		if (srcType.value > 0) endpoint = env.net.urlParam(endpoint, "srcType", srcType.value);
		if (srcId.value) endpoint = env.net.urlParam(endpoint, "srcId", srcId.value);
		return endpoint;
	}

	async function reallyLoadForceInfo():Promise<boolean> {
		let endpoint = addListEndpointParams("api/forceinfo");
		const resp = await env.net.fetch(endpoint);

		if (resp.ok) {
			const page: AgendaWithBlockers<ForceInfo> = await resp.json();
			setForceInfoItems(page);
		}
		return resp.ok;
	}

	const loadForceInfo = debounce(reallyLoadForceInfo, 20);

	async function deleteForceInfo(id:string|number):Promise<any> {
		let endpoint = addListEndpointParams("api/forceinfo/"+id);
		return env.net.fetch(endpoint, "DELETE")
			.then((resp) => {
				if (resp.ok) {
					resp.json().then((page:AgendaWithBlockers<ForceInfo>) => setForceInfoItems(page));
				}
			});
	}

	async function executeForceInfoAction(id:string|number, actionName:string, data:object):Promise<ApiErrorResponse> {
		let endpoint = addListEndpointParams("api/forceinfo/"+id);
		endpoint = env.net.urlParam(endpoint, "action", actionName);
		return env.net.fetch(endpoint, "POST", data)
			.then((resp) => {
				if (resp.ok) {
					// there is a fresh set of force infos in the response
					return resp.json().then((page:AgendaWithBlockers<ForceInfo>) => {
						setForceInfoItems(page);
						const r = <ApiErrorResponse>{
							ok: true,
							status: resp.status
						};
						if (page.meta) r.data = page.meta.result;
						return r;
					});
				} else {
					return resp.json();
				}
			});
	}

	function forceInfoPrev() {
		if (forceInfoPosition.value > 0) {
			forceInfoPosition.value = forceInfoPosition.value - forceInfoPage.value;
		}
		if (forceInfoPosition.value < 0) forceInfoPosition.value = 0;
		loadForceInfo();
	}

	function forceInfoNext() {
		if ((forceInfoPosition.value + forceInfos.value.length) < forceInfoTotal.value) {
			forceInfoPosition.value += forceInfoPage.value;
		}
		loadForceInfo();
	}

	watch([respList, srcType, srcId], () => {
		if (srcType.value >= 0) loadForceInfo();
	});


	return {
		setResponsibles, setForceInfoContext,

		forceInfos, blockers, feedbackForceInSize, loadForceInfo, setForceInfoItems, deleteForceInfo, forceInfoPage,
		forceInfoTotal, forceInfoPosition, forceInfoPrev, forceInfoNext, executeForceInfoAction,
		userBlocks
	}
});